import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  Paper,
  Divider,
  CircularProgress,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import { AddCircleOutline, DeleteOutline, EditOutlined, ArrowUpward, ArrowDownward, Visibility, Save, ColorLens, InfoOutlined } from '@mui/icons-material';
import { ChromePicker } from 'react-color';
import { getSectionContent, updateSectionContent } from '../services/contentService';
import CloudinaryUploadWidget from './CloudinaryUploadWidget'; // Assuming this is the correct path
import { v4 as uuidv4 } from 'uuid';

const commonFontFamilies = [
  'Arial', 'Verdana', 'Helvetica', 'Tahoma', 'Trebuchet MS', 'Times New Roman', 'Georgia', 'Garamond',
  'Courier New', 'Brush Script MT', 'Calibri', 'Candara', 'Optima', 'Gill Sans', 'Futura', 'Avant Garde',
  'Palatino', 'Bookman', 'Lucida Console', 'Monaco', 'Consolas', 'Courier', 'Lucida Handwriting', 'Impact', 'Comic Sans MS'
];

const SECTION_ID = 'aboutUsSection';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`aboutus-tabpanel-${index}`}
      aria-labelledby={`aboutus-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const initialAboutUsData = {
  mainTitle: '',
  mainSubtitle: '',
  storyTitle: '',
  storyParagraph1: '',
  storyParagraph2: '',
  introCarouselImages: [],
  specialistTitle: '',
  specialistParagraph: '',
  keyFeatures: [],
  facilityImage: {
    imageUrl: '',
    altText: '',
    overlayTitle: '',
    overlaySubtitle: '',
    overlayTitleColor: '#FFFFFF',
    overlayTitleFontSize: 24,
    overlayTitleFontFamily: 'Arial',
    overlayTitleFontWeight: 'font-semibold',
    overlaySubtitleColor: '#F0F0F0',
    overlaySubtitleFontSize: 18,
    overlaySubtitleFontFamily: 'Arial',
    overlaySubtitleFontWeight: 'font-normal',
  },
  certificatesTitle: '',
  certificates: [],
  updatedAt: null,

  // Styling properties
  mainTitleColor: '#000000',
  mainTitleFontSize: 32,
  mainTitleFontFamily: 'Arial',
  mainTitleFontWeight: 'font-bold',

  mainSubtitleColor: '#555555',
  mainSubtitleFontSize: 18,
  mainSubtitleFontFamily: 'Arial',
  mainSubtitleFontWeight: 'font-medium',

  storyTitleColor: '#000000',
  storyTitleFontSize: 24,
  storyTitleFontFamily: 'Arial',
  storyTitleFontWeight: 'font-semibold',

  storyTextColor: '#333333',
  storyTextFontSize: 16,
  storyTextFontFamily: 'Arial',
  storyTextFontWeight: 'font-normal',

  specialistTitleColor: '#000000',
  specialistTitleFontSize: 24,
  specialistTitleFontFamily: 'Arial',
  specialistTitleFontWeight: 'font-semibold',

  specialistTextColor: '#333333',
  specialistTextFontSize: 16,
  specialistTextFontFamily: 'Arial',
  specialistTextFontWeight: 'font-normal',

  featureTitleColor: '#000000',
  featureTitleFontSize: 20,
  featureTitleFontFamily: 'Arial',
  featureTitleFontWeight: 'font-medium',

  featureDescriptionColor: '#333333',
  featureDescriptionFontSize: 14,
  featureDescriptionFontFamily: 'Arial',
  featureDescriptionFontWeight: 'font-normal',

  certificatesTitleColor: '#000000',
  certificatesTitleFontSize: 22,
  certificatesTitleFontFamily: 'Arial',
  certificatesTitleFontWeight: 'font-semibold',

  specificFeatureText: 'Highlighting our state of the art features.',
  specificFeatureTextColor: '#1E40AF',
  specificFeatureTextFontSize: 16,
  specificFeatureTextFontFamily: 'Arial',
  specificFeatureTextFontWeight: 'font-normal',

  innovationText: 'Equipped to foster innovation and excellence in every endeavor.',
  innovationTextColor: '#047857',
  innovationTextFontSize: 16,
  innovationTextFontFamily: 'Arial',
  innovationTextFontWeight: 'font-normal',
};

const AboutUsSectionEditor = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [aboutUsData, setAboutUsData] = useState(initialAboutUsData);
  const [activeColorPicker, setActiveColorPicker] = useState(null);
  const [colorPickerPosition, setColorPickerPosition] = useState({ top: 0, left: 0 }); // This might not be used with Dialog
  const [showPreview, setShowPreview] = useState(false);

  const fetchAboutUsData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getSectionContent(SECTION_ID);
      let processedData = {
        ...initialAboutUsData,
        ...(data || {}),
        introCarouselImages: (data && data.introCarouselImages) ? data.introCarouselImages : [],
        keyFeatures: (data && data.keyFeatures) ? data.keyFeatures : [],
        certificates: (data && data.certificates) ? data.certificates : [],
        facilityImage: {
          ...initialAboutUsData.facilityImage,
          ...((data && data.facilityImage) ? data.facilityImage : {}),
        },
      };

      const styleProperties = [
        'mainTitle', 'mainSubtitle', 'storyTitle', 'storyText', 
        'specialistTitle', 'specialistText', 'featureTitle', 'featureDescription',
        'certificatesTitle', 'specificFeatureText', 'innovationText'
      ];

      styleProperties.forEach(prop => {
        const fontSizeKey = `${prop}FontSize`;
        const fontFamilyKey = `${prop}FontFamily`;
        
        // Process Font Size
        let val = processedData[fontSizeKey];
        const defaultFontSize = initialAboutUsData[fontSizeKey];
        if (typeof val === 'string') {
          const parsed = parseFloat(val.replace('px', ''));
          processedData[fontSizeKey] = isNaN(parsed) ? defaultFontSize : parsed;
        } else if (typeof val !== 'number' || isNaN(val)) {
          processedData[fontSizeKey] = defaultFontSize;
        }

        // Process Font Family
        if (!processedData[fontFamilyKey] || typeof processedData[fontFamilyKey] !== 'string') {
          processedData[fontFamilyKey] = initialAboutUsData[fontFamilyKey];
        }
      });

      // Process Facility Image Overlay Styles
      const facilityOverlayProps = ['overlayTitle', 'overlaySubtitle'];
      facilityOverlayProps.forEach(prop => {
        const fontSizeKey = `${prop}FontSize`;
        const fontFamilyKey = `${prop}FontFamily`;

        let val = processedData.facilityImage[fontSizeKey];
        const defaultFontSize = initialAboutUsData.facilityImage[fontSizeKey];
        if (typeof val === 'string') {
          const parsed = parseFloat(val.replace('px', ''));
          processedData.facilityImage[fontSizeKey] = isNaN(parsed) ? defaultFontSize : parsed;
        } else if (typeof val !== 'number' || isNaN(val)) {
          processedData.facilityImage[fontSizeKey] = defaultFontSize;
        }

        if (!processedData.facilityImage[fontFamilyKey] || typeof processedData.facilityImage[fontFamilyKey] !== 'string') {
          processedData.facilityImage[fontFamilyKey] = initialAboutUsData.facilityImage[fontFamilyKey];
        }
      });

      setAboutUsData(processedData);
    } catch (error) {
      toast.error('Failed to load About Us section data.');
      console.error('Error fetching About Us data:', error);
      setAboutUsData(initialAboutUsData);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAboutUsData();
  }, [fetchAboutUsData]);

  const handleTabChange = (event, newValue) => setTabValue(newValue);

  const handleInputChange = (e) => {
    const { name, value: rawValue, type } = e.target;
    let value = rawValue;
    if (name.endsWith('FontSize')) {
      if (rawValue === '' || rawValue === null) {
        value = ''; // Allow user to clear, default handled by frontend or initialData
      } else {
        const parsed = parseFloat(rawValue);
        value = isNaN(parsed) ? rawValue : parsed; // Keep raw if not a number for user correction
      }
    }
    setAboutUsData(prev => ({ ...prev, [name]: value }));
  };

  const handleNestedInputChange = (parentKey, nestedKey, rawValue) => {
    let value = rawValue;
    if (nestedKey.endsWith('FontSize')) {
      if (rawValue === '' || rawValue === null) {
        value = '';
      } else {
        const parsed = parseFloat(rawValue);
        value = isNaN(parsed) ? rawValue : parsed;
      }
    }
    setAboutUsData(prev => ({
      ...prev,
      [parentKey]: {
        ...(prev[parentKey] || {}),
        [nestedKey]: value,
      },
    }));
  };

  const handleColorPickerClick = (colorType) => {
    // No need for event or position with Dialog
    setActiveColorPicker(colorType);
  };

  const handleColorChange = (color) => {
    if (activeColorPicker) {
      // This function is for direct color changes
      setAboutUsData(prev => ({ ...prev, [activeColorPicker]: color.hex }));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Create a deep copy to ensure font sizes are converted for backend before saving
      const dataToSave = JSON.parse(JSON.stringify(aboutUsData));

      const convertFontSizeForBackend = (value) => {
        return typeof value === 'number' ? `${value}px` : value;
      };

      // Convert global styling font sizes
      const globalStyleProperties = [
        'mainTitle', 'mainSubtitle', 'storyTitle', 'storyText', 
        'specialistTitle', 'specialistText', 'featureTitle', 'featureDescription',
        'certificatesTitle', 'specificFeatureText', 'innovationText'
      ];
      globalStyleProperties.forEach(prop => {
        const fontSizeKey = `${prop}FontSize`;
        if (dataToSave[fontSizeKey] !== undefined) {
          dataToSave[fontSizeKey] = convertFontSizeForBackend(dataToSave[fontSizeKey]);
        }
      });

      // Convert facility image overlay font sizes
      const facilityOverlayProps = ['overlayTitle', 'overlaySubtitle'];
      facilityOverlayProps.forEach(prop => {
        const fontSizeKey = `${prop}FontSize`;
        if (dataToSave.facilityImage?.[fontSizeKey] !== undefined) {
          dataToSave.facilityImage[fontSizeKey] = convertFontSizeForBackend(dataToSave.facilityImage[fontSizeKey]);
        }
      });


      await updateSectionContent(SECTION_ID, dataToSave);
      toast.success('About Us section updated successfully!');
      fetchAboutUsData(); // Re-fetch to ensure data consistency
    } catch (error) {
      toast.error('Failed to update About Us section.');
      console.error('Error saving About Us data:', error);
    } finally {
      setSaving(false);
    }
  };

  const togglePreview = () => setShowPreview(!showPreview);

  // Generic Array Item Management
  const handleAddItem = (arrayName, newItemTemplate) => {
    const currentArray = aboutUsData[arrayName] || [];
    const newItem = { ...newItemTemplate, id: uuidv4(), displayOrder: currentArray.length };
    setAboutUsData(prev => {
      const prevArray = prev[arrayName] || [];
      return { ...prev, [arrayName]: [...prevArray, newItem] };
    });
  };

  const handleUpdateItem = (arrayName, index, updatedFields) => {
    setAboutUsData(prev => ({
      ...prev,
      [arrayName]: prev[arrayName].map((item, i) => i === index ? { ...item, ...updatedFields } : item),
    }));
  };

  const handleDeleteItem = (arrayName, index) => {
    setAboutUsData(prev => ({
      ...prev,
      [arrayName]: prev[arrayName].filter((_, i) => i !== index).map((item, i) => ({ ...item, displayOrder: i })),
    }));
  };

  const handleMoveItem = (arrayName, index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= aboutUsData[arrayName].length) return;
    const items = [...aboutUsData[arrayName]];
    const itemToMove = items[index];
    items.splice(index, 1);
    items.splice(newIndex, 0, itemToMove);
    setAboutUsData(prev => ({ ...prev, [arrayName]: items.map((item, i) => ({ ...item, displayOrder: i })) }));
  };

  const renderArrayManager = (title, arrayName, itemTemplate, renderItemFields) => (
    <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">{title}</Typography>
        <Button variant="outlined" size="small" startIcon={<AddCircleOutline sx={{ fontSize: 20 }} />} onClick={() => handleAddItem(arrayName, itemTemplate)}>
          Add Item
        </Button>
      </Box>
      {aboutUsData[arrayName] && aboutUsData[arrayName].map((item, index) => (
          <Paper key={item.id || index} elevation={1} sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1">Item {index + 1}</Typography>
              <Box>
                <IconButton onClick={() => handleMoveItem(arrayName, index, 'up')} disabled={index === 0} size="small">
                  <ArrowUpward sx={{ fontSize: 20 }} />
                </IconButton>
                <IconButton onClick={() => handleMoveItem(arrayName, index, 'down')} disabled={index === aboutUsData[arrayName].length - 1} size="small">
                  <ArrowDownward sx={{ fontSize: 20 }} />
                </IconButton>
                <IconButton onClick={() => handleDeleteItem(arrayName, index)} color="error" size="small">
                  <DeleteOutline sx={{ fontSize: 20 }} />
                </IconButton>
              </Box>
            </Box>
            {renderItemFields(item, index)}
          </Paper>
        ))}
    </Paper>
  );

  // Loading state check
  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 120px)' }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">About Us Section Editor</Typography>
        <Box>
          <Tooltip title="Preview">
            <IconButton onClick={togglePreview} sx={{ mr: 1 }} size="medium"><Visibility sx={{ fontSize: 24 }} /></IconButton>
          </Tooltip>
          <Button variant="contained" color="primary" onClick={handleSave} disabled={saving} 
            startIcon={saving ? <CircularProgress size={20} /> : <Save sx={{ fontSize: 20 }} />}
            sx={{ py: 0.8, px: 2, borderRadius: '8px' }} // Adjusted padding and border radius
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Box>

      <Tabs value={tabValue} onChange={handleTabChange} aria-label="about us section tabs" variant="scrollable" scrollButtons="auto">
        <Tab label="Main Content" />
        <Tab label="Story Content" />
        <Tab label="Intro Carousel" />
        <Tab label="Specialist & Features" />
        <Tab label="Facility Details" />
        <Tab label="Certificates" />
        <Tab label="Styling" />
      </Tabs>

      {/* Main Content Tab */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}><TextField fullWidth label="Main Title" name="mainTitle" value={aboutUsData.mainTitle} onChange={handleInputChange} size="small" /></Grid>
          <Grid item xs={12} md={6}><TextField fullWidth label="Main Subtitle" name="mainSubtitle" value={aboutUsData.mainSubtitle} onChange={handleInputChange} multiline rows={3} size="small" /></Grid>
        </Grid>
      </TabPanel>

      {/* Story & Main Carousel Tab */}
      <TabPanel value={tabValue} index={1}>
        <TextField fullWidth label="Story Title" name="storyTitle" value={aboutUsData.storyTitle} onChange={handleInputChange} sx={{ mb: 2 }} size="small" />
        <TextField fullWidth label="Story Paragraph 1" name="storyParagraph1" value={aboutUsData.storyParagraph1} onChange={handleInputChange} multiline rows={4} sx={{ mb: 2 }} size="small" />
        <TextField fullWidth label="Story Paragraph 2" name="storyParagraph2" value={aboutUsData.storyParagraph2} onChange={handleInputChange} multiline rows={4} sx={{ mb: 2 }} size="small" />
      </TabPanel>

      {/* Introductory Carousel Tab */}
      <TabPanel value={tabValue} index={2}>
        {renderArrayManager(
          'Introductory Carousel Images (Max 7 recommended)',
          'introCarouselImages',
          { id: uuidv4(), imageUrl: '', altText: '', displayOrder: 0 },
          (item, index) => (
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  label="Image URL"
                  value={item.imageUrl}
                  onChange={(e) => handleUpdateItem('introCarouselImages', index, { imageUrl: e.target.value })}
                  sx={{ mb: 1 }}
                  size="small"
                />
                <CloudinaryUploadWidget
                  onUploadSuccess={(url) => {
                    handleUpdateItem('introCarouselImages', index, { imageUrl: url });
                  }}
                  folderName={`about_us/intro_carousel`}
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  label="Alt Text"
                  value={item.altText}
                  onChange={(e) => handleUpdateItem('introCarouselImages', index, { altText: e.target.value })}
                  size="small"
                />
              </Grid>
              {item.imageUrl && (
                <Grid item xs={12} md={2}>
                  <img
                    src={item.imageUrl}
                    alt={item.altText || 'Intro image'}
                    style={{ width: '100px', height: 'auto', marginTop: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                  />
                </Grid>
              )}
            </Grid>
          )
        )}
      </TabPanel>

      {/* Specialist & Features Tab */}
      <TabPanel value={tabValue} index={3}>
        <TextField fullWidth label="Specialist Title" name="specialistTitle" value={aboutUsData.specialistTitle} onChange={handleInputChange} sx={{ mb: 2 }} size="small" />
        <TextField fullWidth label="Specialist Paragraph" name="specialistParagraph" value={aboutUsData.specialistParagraph} onChange={handleInputChange} multiline rows={4} sx={{ mb: 2 }} size="small" />

        {/* Specific Feature Text Input */}
        <TextField 
          fullWidth 
          label="Highlight Text (e.g., 'state of the art features')" 
          name="specificFeatureText" 
          value={aboutUsData.specificFeatureText} 
          onChange={handleInputChange} 
          multiline 
          rows={2} 
          sx={{ mb: 2 }} 
          helperText="This text will use the 'Specific Feature Text Color' from the Styling tab."
          size="small"
        />

        {/* Innovation Text Input */}
        <TextField 
          fullWidth 
          label="Secondary Highlight Text (e.g., 'Equipped to foster innovation...')" 
          name="innovationText" 
          value={aboutUsData.innovationText} 
          onChange={handleInputChange} 
          multiline 
          rows={3} 
          sx={{ mb: 2 }}
          helperText="This text will use the 'Innovation Text Color' from the Styling tab."
          size="small"
        />

        {renderArrayManager('Key Features', 'keyFeatures', { title: '', description: '' }, (item, index) => (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}><TextField fullWidth label="Feature Title" value={item.title} onChange={(e) => handleUpdateItem('keyFeatures', index, { title: e.target.value })} size="small" /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Feature Description" value={item.description} onChange={(e) => handleUpdateItem('keyFeatures', index, { description: e.target.value })} multiline rows={2} size="small" /></Grid>
          </Grid>
        ))}
      </TabPanel>

      {/* Facility Details Tab */}
      <TabPanel value={tabValue} index={4}>
        <Typography variant="h6" gutterBottom>Facility Details</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Facility Image URL" value={aboutUsData.facilityImage.imageUrl} onChange={(e) => handleNestedInputChange('facilityImage', 'imageUrl', e.target.value)} sx={{ mb: 1 }} size="small" />
            <CloudinaryUploadWidget 
              onUploadSuccess={(url) => handleNestedInputChange('facilityImage', 'imageUrl', url)}
              folderName="about_us/facility"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Facility Image Alt Text" value={aboutUsData.facilityImage.altText} onChange={(e) => handleNestedInputChange('facilityImage', 'altText', e.target.value)} size="small" />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Overlay Title" value={aboutUsData.facilityImage.overlayTitle} onChange={(e) => handleNestedInputChange('facilityImage', 'overlayTitle', e.target.value)} size="small" />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Overlay Subtitle" value={aboutUsData.facilityImage.overlaySubtitle} onChange={(e) => handleNestedInputChange('facilityImage', 'overlaySubtitle', e.target.value)} size="small" />
          </Grid>
          {aboutUsData.facilityImage.imageUrl && (
            <Grid item xs={12}>
              <img src={aboutUsData.facilityImage.imageUrl} alt={aboutUsData.facilityImage.altText || 'Facility image'} style={{ width: '200px', height: 'auto', marginTop: '10px', border: '1px solid #ccc', borderRadius: '4px' }} />
            </Grid>
          )}
        </Grid>
      </TabPanel>

      {/* Certificates Tab (Index 6 - previously 4 or 5) */}
      <TabPanel value={tabValue} index={5}>
        <TextField fullWidth label="Certificates Title" name="certificatesTitle" value={aboutUsData.certificatesTitle} onChange={handleInputChange} sx={{ mb: 2 }} size="small" />
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddCircleOutline sx={{ fontSize: 20 }} />}
          onClick={() => {
            if ((aboutUsData.certificates || []).length < 5) {
                handleAddItem('certificates', { title: '', description: '', imageUrl: '', altText: '' });
            } else {
                toast.warn('Maximum of 5 certificates allowed.');
            }
          }}
          sx={{ mb: 2 }}
          disabled={(aboutUsData.certificates || []).length >= 5}
        >
          Add Certificate
        </Button>
        {renderArrayManager(
          'Certificates (Max 5)',
          'certificates',
          { title: '', description: '', imageUrl: '', altText: '' },
          (item, index) => (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Certificate Title"
                  value={item.title}
                  onChange={(e) => handleUpdateItem('certificates', index, { title: e.target.value })}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Certificate Description"
                  value={item.description}
                  onChange={(e) => handleUpdateItem('certificates', index, { description: e.target.value })}
                  multiline
                  rows={2}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Image URL"
                  value={item.imageUrl}
                  onChange={(e) => handleUpdateItem('certificates', index, { imageUrl: e.target.value })}
                  sx={{ mb: 1 }}
                  size="small"
                />
                <CloudinaryUploadWidget
                  onUploadSuccess={(url) => {
                    handleUpdateItem('certificates', index, { imageUrl: url });
                  }}
                  folderName={`about_us/certificates`}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Alt Text"
                  value={item.altText}
                  onChange={(e) => handleUpdateItem('certificates', index, { altText: e.target.value })}
                  size="small"
                />
              </Grid>
              {item.imageUrl && (
                <Grid item xs={12}>
                  <img
                    src={item.imageUrl}
                    alt={item.altText || 'Certificate image'}
                    style={{ width: '100px', height: 'auto', marginTop: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                  />
                </Grid>
              )}
            </Grid>
          )
        )}
      </TabPanel>

      {/* Styling Tab (Index 6) */}
      <TabPanel value={tabValue} index={6}>
        <Typography variant="h5" gutterBottom sx={{ mb: 2, fontWeight: 'medium' }}>Global Text Styling</Typography>
        
        {[ 
          { key: 'mainTitle', label: 'Main Title' }, 
          { key: 'mainSubtitle', label: 'Main Subtitle' }, 
          { key: 'storyTitle', label: 'Story Title' }, 
          { key: 'storyText', label: 'Story Paragraphs' }, 
          { key: 'specialistTitle', label: 'Specialist Title' }, 
          { key: 'specialistText', label: 'Specialist Paragraph' }, 
          { key: 'featureTitle', label: 'Key Feature Title' }, 
          { key: 'featureDescription', label: 'Key Feature Description' },
          { key: 'certificatesTitle', label: 'Certificates Section Title' },
          { key: 'specificFeatureText', label: 'Specific Feature Highlight Text' },
          { key: 'innovationText', label: 'Innovation Highlight Text' }
        ].map(({key, label}) => (
          <Paper key={key} elevation={2} sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 2, fontSize: '1.1rem' }}>{label}</Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <Typography gutterBottom sx={{fontSize: '0.875rem'}}>Color</Typography>
                <Button fullWidth variant="outlined" onClick={() => handleColorPickerClick(`${key}Color`)} 
                  startIcon={<ColorLens sx={{ fontSize: 20 }} />} 
                  sx={{borderColor: aboutUsData[`${key}Color`], color: aboutUsData[`${key}Color`], justifyContent: 'flex-start', textTransform: 'none', padding: '6px 8px' }}
                  size="small" // Make button smaller
                >
                  <Box sx={{width: 20, height: 20, backgroundColor: aboutUsData[`${key}Color`], border: '1px solid grey', borderRadius: '4px', mr: 1}} /> 
                  Pick Color
                </Button>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField 
                  fullWidth 
                  label="Font Size (px)" 
                  name={`${key}FontSize`} 
                  type="number"
                  value={aboutUsData[`${key}FontSize`] || ''} 
                  onChange={handleInputChange} 
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  helperText="e.g., 16"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel id={`${key}FontFamily-label`}>Font Family</InputLabel>
                  <Select
                    labelId={`${key}FontFamily-label`}
                    name={`${key}FontFamily`}
                    value={aboutUsData[`${key}FontFamily`] || 'Arial'}
                    label="Font Family"
                    onChange={handleInputChange}
                  >
                    {commonFontFamilies.map(font => <MenuItem key={font} value={font}>{font}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel id={`${key}FontWeight-label`}>Font Weight</InputLabel>
                  <Select
                    labelId={`${key}FontWeight-label`}
                    name={`${key}FontWeight`}
                    value={aboutUsData[`${key}FontWeight`] || 'font-normal'}
                    label="Font Weight"
                    onChange={handleInputChange}
                  >
                    <MenuItem value="font-light">Light</MenuItem>
                    <MenuItem value="font-normal">Normal</MenuItem>
                    <MenuItem value="font-medium">Medium</MenuItem>
                    <MenuItem value="font-semibold">Semibold</MenuItem>
                    <MenuItem value="font-bold">Bold</MenuItem>
                    <MenuItem value="font-extrabold">Extra Bold</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>
        ))}

        {/* Facility Image Overlay Styling */}
        <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 2, fontSize: '1.1rem' }}>Facility Image Overlay</Typography>
          {[ 
            { key: 'overlayTitle', label: 'Overlay Title' }, 
            { key: 'overlaySubtitle', label: 'Overlay Subtitle' } 
          ].map(({key, label}) => (
            <Box key={key} sx={{ mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom sx={{fontWeight: 'medium'}}>{label}</Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6} md={3}>
                  <Typography gutterBottom sx={{fontSize: '0.875rem'}}>Color</Typography>
                  <Button fullWidth variant="outlined" onClick={() => handleColorPickerClick(`facilityImage.${key}Color`)} 
                    startIcon={<ColorLens sx={{ fontSize: 20 }} />} 
                    sx={{borderColor: aboutUsData.facilityImage?.[`${key}Color`], color: aboutUsData.facilityImage?.[`${key}Color`], justifyContent: 'flex-start', textTransform: 'none', padding: '6px 8px' }}
                    size="small" // Make button smaller
                  >
                    <Box sx={{width: 20, height: 20, backgroundColor: aboutUsData.facilityImage?.[`${key}Color`], border: '1px solid grey', borderRadius: '4px', mr: 1}} /> 
                    Pick Color
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField 
                    fullWidth 
                    label="Font Size (px)" 
                    name={`${key}FontSize`}
                    type="number"
                    value={aboutUsData.facilityImage?.[`${key}FontSize`] || ''} 
                    onChange={(e) => handleNestedInputChange('facilityImage', `${key}FontSize`, e.target.value)} 
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    helperText="e.g., 24"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel id={`facilityImage-${key}FontFamily-label`}>Font Family</InputLabel>
                    <Select
                      labelId={`facilityImage-${key}FontFamily-label`}
                      name={`${key}FontFamily`}
                      value={aboutUsData.facilityImage?.[`${key}FontFamily`] || 'Arial'}
                      label="Font Family"
                      onChange={(e) => handleNestedInputChange('facilityImage', `${key}FontFamily`, e.target.value)}
                    >
                      {commonFontFamilies.map(font => <MenuItem key={font} value={font}>{font}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel id={`facilityImage-${key}FontWeight-label`}>Font Weight</InputLabel>
                    <Select
                      labelId={`facilityImage-${key}FontWeight-label`}
                      name={`${key}FontWeight`}
                      value={aboutUsData.facilityImage?.[`${key}FontWeight`] || 'font-normal'}
                      label="Font Weight"
                      onChange={(e) => handleNestedInputChange('facilityImage', `${key}FontWeight`, e.target.value)}
                    >
                      <MenuItem value="font-light">Light</MenuItem>
                      <MenuItem value="font-normal">Normal</MenuItem>
                      <MenuItem value="font-medium">Medium</MenuItem>
                      <MenuItem value="font-semibold">Semibold</MenuItem>
                      <MenuItem value="font-bold">Bold</MenuItem>
                      <MenuItem value="font-extrabold">Extra Bold</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          ))}
        </Paper>

        {activeColorPicker && (
          <Dialog open={Boolean(activeColorPicker)} onClose={() => setActiveColorPicker(null)} PaperProps={{ sx: { overflow: 'visible' } }}>
            <DialogTitle>Pick Color</DialogTitle>
            <DialogContent sx={{ overflow: 'visible', width: '250px' }}> {/* Control width of dialog content */}
              <ChromePicker 
                color={activeColorPicker.includes('.') ? aboutUsData[activeColorPicker.split('.')[0]][activeColorPicker.split('.')[1]] : aboutUsData[activeColorPicker]} 
                onChangeComplete={(color) => {
                  if (activeColorPicker.includes('.')) {
                    const [parentKey, childKey] = activeColorPicker.split('.');
                    handleNestedInputChange(parentKey, childKey, color.hex);
                  } else {
                    handleColorChange(color);
                  }
                }} 
                width="100%" // ChromePicker takes 100% of controlled DialogContent width
                disableAlpha // Usually color pickers for UI don't need alpha
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setActiveColorPicker(null)}>Done</Button>
            </DialogActions>
          </Dialog>
        )}
      </TabPanel>

      {/* Preview Dialog (Simplified) */}
      <Dialog open={showPreview} onClose={togglePreview} fullWidth maxWidth="lg">
        <DialogTitle>About Us Section Preview</DialogTitle>
        <DialogContent>
          <Paper elevation={0} sx={{ p: 2, border: '1px dashed grey' }}>
            <Typography variant="h4" sx={{ color: aboutUsData.mainTitleColor, fontSize: aboutUsData.mainTitleFontSize }}>{aboutUsData.mainTitle || 'Main Title Preview'}</Typography>
            <Typography variant="subtitle1" sx={{ color: aboutUsData.mainSubtitleColor, fontSize: aboutUsData.mainSubtitleFontSize }}>{aboutUsData.mainSubtitle || 'Main Subtitle Preview'}</Typography>
            <Divider sx={{ my: 2 }} />
            {/* Add more preview elements here based on aboutUsData */}
            <Typography sx={{ fontStyle: 'italic' }}>More detailed preview elements would go here, reflecting the current state of 'aboutUsData'.</Typography>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={togglePreview}>Close Preview</Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default AboutUsSectionEditor;